import { array as codecArray } from 'purify-ts/Codec';
import { makeResponses } from '../response_factory';
import { utils as coreUtils, paginated, requestParams } from '@codegrade/api-core';
import { AccessPassCouponUsage, CourseCouponUsage, CoursePurchase, ExtendedUser, LoginUserData, LogoutResponse, LogoutUserData, PatchUserData, RegisterUserData, SessionRestrictionData, TenantCouponUsage, User, UserAccessPass, UserLoginResponse, } from '@codegrade/api-models';
import { parsers } from '@codegrade/utils';
const makeResult = coreUtils.makeResult;
// eslint-disable-next-line @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars
const parseJsonResponse = /* #__PURE__ */ parsers.parseJsonResponse;
export function user(config) {
    const patchResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(ExtendedUser)),
    });
    const getCouponUsagesResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ paginated.makeResult(
        /* #__PURE__ */ parseJsonResponse(
        /* #__PURE__ */ codecArray(
        /* #__PURE__ */ parsers.taggedUnion('scope', [
            ['course', CourseCouponUsage],
            ['tenant', TenantCouponUsage],
            ['access-plan', AccessPassCouponUsage],
        ])))),
    });
    const getTransactionsResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ paginated.makeResult(
        /* #__PURE__ */ parseJsonResponse(
        /* #__PURE__ */ codecArray(
        /* #__PURE__ */ parsers.taggedUnion('scope', [
            ['course', CoursePurchase],
            ['access-plan', UserAccessPass],
        ])))),
    });
    const loginResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(UserLoginResponse)),
    });
    const getResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(ExtendedUser)),
    });
    const registerResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(UserLoginResponse)),
    });
    const restrictResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(UserLoginResponse)),
    });
    const logoutResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(LogoutResponse)),
    });
    const searchResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ paginated.makeResult(
        /* #__PURE__ */ parseJsonResponse(/* #__PURE__ */ codecArray(User))),
    });
    return Object.freeze({
        /**
         * Update the attributes of a user.
         *
         * @param arg The arguments to the function.
         * @param arg.userId The id of the user you want to change. Currently this can only be your own user id.
         * @param arg.requestBody The body of the request.
         * @returns The updated user.
         */
        patch: async ({ userId, requestBody, }) => {
            const result = await requestParams(config, 'PATCH', `/api/v1/users/${userId}`, patchResponse.v, JSON.stringify(PatchUserData.encode(requestBody)));
            return result;
        },
        /**
         * Get all the coupons used for the specified user.
         *
         * @param arg The arguments to the function.
         * @param arg.userId The user to get the coupons used for.
         * @param arg.pageSize The size of a single page, maximum is 50.
         * @param arg.nextToken The next token, to retrieve a next page.
         * @returns All coupons used for the given user.
         */
        getCouponUsages: async ({ userId, pageSize = undefined, nextToken = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/users/${userId}/coupon_usages/`, getCouponUsagesResponse.v, undefined, {
                'page-size': pageSize,
                'next-token': nextToken,
            });
            return result;
        },
        /**
         * Get all transactions for the specified user.
         *
         * @param arg The arguments to the function.
         * @param arg.userId The user to get the transactions for.
         * @param arg.pageSize The size of a single page, maximum is 50.
         * @param arg.nextToken The next token, to retrieve a next page.
         * @returns All transactions for the given user.
         */
        getTransactions: async ({ userId, pageSize = undefined, nextToken = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/users/${userId}/transactions/`, getTransactionsResponse.v, undefined, {
                'page-size': pageSize,
                'next-token': nextToken,
            });
            return result;
        },
        /**
         * Login using your username and password.
         *
         * @param arg The arguments to the function.
         * @param arg.requestBody The body of the request.
         * @returns A response containing the JSON serialized user
         */
        login: async ({ requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/login`, loginResponse.v, JSON.stringify(LoginUserData.encode(requestBody)));
            return result;
        },
        /**
         * Get the info of the currently logged in user.
         *
         * @returns A response containing the JSON serialized user
         */
        get: async () => {
            const result = await requestParams(config, 'GET', `/api/v1/login`, getResponse.v, undefined);
            return result;
        },
        /**
         * Create a new user.
         *
         * @param arg The arguments to the function.
         * @param arg.requestBody The body of the request.
         * @returns The registered user and an `access_token` that can be used to perform requests as this new user.
         */
        register: async ({ requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/user`, registerResponse.v, JSON.stringify(RegisterUserData.encode(requestBody)));
            return result;
        },
        /**
         * Revoke a given token.
         *
         * @param arg The arguments to the function.
         * @param arg.requestBody The body of the request.
         * @returns An new token that has the given restrictions added.
         */
        restrict: async ({ requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/token/restrict`, restrictResponse.v, JSON.stringify(SessionRestrictionData.encode(requestBody)));
            return result;
        },
        /**
         * Revoke a given token.
         *
         * @param arg The arguments to the function.
         * @param arg.requestBody The body of the request.
         * @returns An empty 200 response.
         */
        logout: async ({ requestBody, }) => {
            const body = coreUtils.toFormData(LogoutUserData.encode(requestBody));
            const result = await requestParams(config, 'POST', `/api/v1/token/revoke`, logoutResponse.v, body);
            return result;
        },
        /**
         * Search for a user by name and username.
         *
         * @param arg The arguments to the function.
         * @param arg.q The string to search for, all SQL wildcard are escaped and spaces are replaced by wildcards.
         * @param arg.excludeCourse Exclude all users that are in the given course from the search results. You need the permission `can_list_course_users` on this course to use this parameter.
         * @param arg.pageSize The size of a single page, maximum is 50.
         * @param arg.nextToken The next token, to retrieve a next page.
         * @returns The users that match the given query string.
         */
        search: async ({ q, excludeCourse = undefined, pageSize = undefined, nextToken = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/users/`, searchResponse.v, undefined, {
                q,
                exclude_course: excludeCourse,
                'page-size': pageSize,
                'next-token': nextToken,
            });
            return result;
        },
    });
}
